import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  Collapse,
  Typography,
  Autocomplete,
  Tabs,
  Tab,
  Button,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Alert,
} from "@mui/material";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { useChannels } from "../hooks/useChannels";
import { useUsers } from "../hooks/useUsers";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { useTypes } from "../hooks/useTypes";
import { usePositions } from "../hooks/usePositions";
import { useDeliveryMethods } from "../hooks/useDeliveryMethods";

const STATUS_OPTIONS = [
  { value: 0, label: "Pendente" },
  { value: 1, label: "Em Atendimento" },
  { value: 2, label: "Concluída" },
  { value: 3, label: "Contato Futuro" },
  { value: 4, label: "Perdida" },
  { value: 5, label: "Cancelada" },
];

const SCHEDULE_UNIT_OPTIONS = [
  { value: "minutes", label: "Minutos" },
  { value: "hours", label: "Horas" },
  { value: "days", label: "Dias" },
];

const mapBasic = (rows) =>
  (rows || []).map((r) => ({
    value: Number(r?.id ?? r?.value ?? r?.codigo ?? r),
    label: String(
      r?.nome ?? r?.name ?? r?.descricao ?? r?.email ?? r?.label ?? r?.id ?? r
    ),
  }));

const addStaticOptions = (fieldOptions, dynamicOptions) => {
  const staticOpts = fieldOptions
    ? Object.entries(fieldOptions).map(([value, label]) => ({
        value: isNaN(Number(value)) ? value : Number(value),
        label: String(label),
      }))
    : [];
  const map = new Map();
  [...staticOpts, ...dynamicOptions].forEach((o) => {
    map.set(String(o.value), o);
  });
  return [...map.values()];
};

function flattenTypes(list) {
  const out = [];
  const walk = (node, parentId = null) => {
    const id = node.id;
    out.push({ ...node, id_pai: node.id_pai ?? parentId ?? null });
    if (Array.isArray(node.children)) node.children.forEach((ch) => walk(ch, id));
  };
  if (Array.isArray(list)) {
    list.forEach((n) => (n.children ? walk(n, null) : out.push(n)));
  }
  return out;
}

const getLevelFromName = (name, fallback = 1) => {
  const m = String(name).match(/(\d+)\s*$/);
  return m ? Number(m[1]) : fallback;
};

const findParentKeyForSubtipo = (entriesFull, level) => {
  if (level === 1) {
    const k = entriesFull.find(([, f]) => f?.object === "tiposdeleads");
    return k?.[0] || null;
  }
  const prevLevel = level - 1;
  let best = null;
  for (const [k, f] of entriesFull) {
    if (f?.object === "subtiposdeleads") {
      const l = getLevelFromName(k, 1);
      if (l === prevLevel) {
        best = k;
        break;
      }
    }
  }
  if (!best) {
    const k = entriesFull.find(([, f]) => f?.object === "subtiposdeleads");
    return k?.[0] || null;
  }
  return best;
};

const isValidEmail = (s) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

const formatVariableToken = (fmt, key) => {
  const f = String(fmt || "").trim();
  if (!f) return `{{${key}}}`;
  if (f.includes("{key}")) return f.replaceAll("{key}", key);
  if (f.includes("key")) return f.replace("key", key);
  return `{{${key}}}`;
};

function buildPreviewHtml(docOrFragment) {
  const raw = String(docOrFragment || "");
  const hasHtmlTag = /<\s*html[\s>]/i.test(raw);
  if (hasHtmlTag) return raw;

  return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>Preview</title>
</head>
<body style="margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;background:#f6f7fb;">
${raw}
</body>
</html>`;
}

function isFullHtmlDocument(raw) {
  const s = String(raw || "");
  return /<!doctype\s+html/i.test(s) || /<\s*html[\s>]/i.test(s) || /<\s*head[\s>]/i.test(s);
}

/** ========= Fix global "ResizeObserver loop..." overlay (CRA/React error overlay) ========= */
function useIgnoreResizeObserverLoopError() {
  useEffect(() => {
    const handler = (event) => {
      const msg = String(event?.message || event?.error?.message || "");
      if (msg.includes("ResizeObserver loop completed with undelivered notifications")) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
        return false;
      }
      return undefined;
    };

    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);
}

/** ===================== Rich Editor (ReactQuill + HTML paste + Preview + Variables) ===================== */
function RichEditorWithHtml({
  fieldName,
  label,
  value,
  onChange,
  onLabel,
  helperText,
  errorText,
  required,
  variables = [],
  varFormat = "{{key}}",
  height = 360,
}) {
  const quillRef = useRef(null);

  const [rawHtml, setRawHtml] = useState(String(value || ""));
  const fullDoc = useMemo(() => isFullHtmlDocument(rawHtml), [rawHtml]);
  const [tab, setTab] = useState(() => (isFullHtmlDocument(value) ? 1 : 0));

  useEffect(() => {
    if (fullDoc && tab === 0) setTab(1);
  }, [fullDoc, tab]);

  useEffect(() => {
    const next = String(value || "");
    setRawHtml(next);
    if (isFullHtmlDocument(next) && tab === 0) setTab(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const [anchorVar, setAnchorVar] = useState(null);
  const openVar = Boolean(anchorVar);

  const setSafeLabel = (html) => {
    const len = String(html || "").length;
    const summary = len ? `HTML definido (${len} chars)` : "";
    onLabel?.(fieldName, summary);
  };

  const commitRaw = (next) => {
    const s = String(next || "");
    setRawHtml(s);
    onChange(s);
    setSafeLabel(s);
  };

  const handleTabChange = (_, v) => {
    if (v === 0 && fullDoc) {
      setTab(1);
      return;
    }
    setTab(v);
  };

  const insertToken = (token) => {
    if (!token) return;

    if (tab === 0 && !fullDoc) {
      const q = quillRef.current?.getEditor?.();
      if (q) {
        const range = q.getSelection(true);
        const idx = range?.index ?? q.getLength();
        q.insertText(idx, token);
        q.setSelection(idx + token.length, 0);
        const next = q.root?.innerHTML ?? "";
        commitRaw(next);
      } else {
        commitRaw(`${String(rawHtml || "")}${token}`);
      }
      return;
    }

    commitRaw(`${String(rawHtml || "")}${token}`);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">
          {label}
          {required ? " *" : ""}
        </Typography>

        {Array.isArray(variables) && variables.length > 0 && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setAnchorVar(e.currentTarget)}
            >
              Variáveis
            </Button>
            <Popover
              open={openVar}
              anchorEl={anchorVar}
              onClose={() => setAnchorVar(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ width: 360, maxWidth: "90vw", p: 1 }}>
                <Typography variant="subtitle2" sx={{ px: 1, pb: 1 }}>
                  Clique para inserir no conteúdo
                </Typography>
                <Divider />
                <List dense sx={{ maxHeight: 320, overflow: "auto" }}>
                  {variables.map((v, idx) => {
                    const key = v?.key ?? v?.value ?? "";
                    const lbl = v?.label ?? key;
                    const token = formatVariableToken(varFormat, key);
                    return (
                      <ListItemButton
                        key={`${key}-${idx}`}
                        onClick={() => {
                          insertToken(token);
                          setAnchorVar(null);
                        }}
                      >
                        <ListItemText
                          primary={lbl}
                          secondary={token}
                          primaryTypographyProps={{ noWrap: true }}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            </Popover>
          </>
        )}
      </Box>

      {fullDoc && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Este conteúdo parece ser um <b>HTML completo</b> (doctype/html/head). O modo{" "}
          <b>EDITOR</b> altera o HTML. Use <b>HTML (COLAR)</b> para manter fidelidade.
        </Alert>
      )}

      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 1 }}>
        <Tab label="EDITOR" disabled={fullDoc} />
        <Tab label="HTML (COLAR)" />
        <Tab label="PRÉ-VISUALIZAR" />
      </Tabs>

      {tab === 0 && (
        <Box
          sx={{
            "& .ql-container": { borderRadius: 1, minHeight: height },
            "& .ql-toolbar": { borderTopLeftRadius: 4, borderTopRightRadius: 4 },
            border: "1px solid",
            borderColor: errorText ? "error.main" : "divider",
            borderRadius: 1,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={fullDoc ? "" : rawHtml || ""}
            onChange={(html) => {
              if (!fullDoc) commitRaw(html);
            }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                ["link"],
                ["clean"],
              ],
            }}
            formats={[
              "header",
              "bold",
              "italic",
              "underline",
              "strike",
              "list",
              "bullet",
              "align",
              "link",
            ]}
          />
        </Box>
      )}

      {tab === 1 && (
        <TextField
          label="Cole o HTML aqui"
          value={rawHtml}
          onChange={(e) => commitRaw(e.target.value)}
          fullWidth
          multiline
          minRows={10}
          error={!!errorText}
          helperText={
            errorText ||
            "Cole HTML (documento completo ou fragmento) e use a aba Pré-visualizar."
          }
        />
      )}

      {tab === 2 && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: errorText ? "error.main" : "divider",
            borderRadius: 1,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          <iframe
            title="preview"
            sandbox=""
            srcDoc={buildPreviewHtml(rawHtml)}
            style={{
              width: "100%",
              height: 520,
              border: 0,
              display: "block",
              background: "#fff",
            }}
          />
        </Box>
      )}

      <Collapse in={Boolean(errorText || helperText)}>
        <Typography
          variant="caption"
          color={errorText ? "error" : "text.secondary"}
          sx={{ mt: 0.5, display: "block" }}
        >
          {errorText || helperText}
        </Typography>
      </Collapse>
    </Box>
  );
}

/** ===================== Recipients Field (to/cc) ===================== */
function RecipientsField({
  label,
  value,
  onChange,
  onLabel,
  helperText,
  errorText,
  required,
  users = [],
  positions = [],
  mode = "to",
}) {
  const normalized = useMemo(() => {
    const v = value;
    if (!v) return [];
    if (Array.isArray(v)) return v;
    return [];
  }, [value]);

  const options = useMemo(() => {
    const out = [];

    out.push({ group: "Atalhos", label: "Cliente (e-mail do cliente)", value: { type: "client" } });
    out.push({ group: "Atalhos", label: "Usuário da solicitação", value: { type: "request_user" } });

    (positions || []).forEach((p) => {
      const id = Number(p?.id);
      const name = String(p?.nome ?? p?.name ?? p?.descricao ?? `Cargo #${id}`);
      out.push({ group: "Cargos", label: name, value: { type: "position", id, name } });
    });
    
    (users || []).forEach((u) => {
      const id = Number(u?.id);
      const name = String(u?.nome ?? u?.name ?? u?.email ?? `Usuário #${id}`);
      const email = String(u?.email ?? "");
      out.push({
        group: "Usuários",
        label: email ? `${name} <${email}>` : name,
        value: { type: "user", id, email: email || undefined, name },
      });
    });

    return out;
  }, [users, positions]);

  const labelsFromValue = (arr) => {
    const parts = (arr || []).map((it) => {
      if (!it) return "";
      if (it.type === "client") return "Cliente";
      if (it.type === "request_user") return "Usuário da solicitação";
      if (it.type === "user")
        return it.email ? `${it.name || "Usuário"} <${it.email}>` : `${it.name || "Usuário"}`;
      if (it.type === "position") return `Cargo: ${it.name || it.id}`;
      if (it.type === "email") return String(it.email || "");
      return "";
    });
    return parts.filter(Boolean);
  };

  const normalizeInputToValue = (input) => {
    const s = String(input || "").trim();
    if (!s) return null;
    const m = s.match(/<([^>]+)>/);
    const email = m ? m[1].trim() : s;
    if (isValidEmail(email)) return { type: "email", email };
    return null;
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={options}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label || "")}
      filterSelectedOptions
      value={normalized.map((v) => {
        const found = options.find((o) => {
          const a = o.value;
          if (!a || !v) return false;
          if (a.type !== v.type) return false;
          if (a.type === "user") return Number(a.id) === Number(v.id);
          if (a.type === "position") return Number(a.id) === Number(v.id);
          if (a.type === "email") return String(a.email || "") === String(v.email || "");
          return a.type === v.type;
        });
        return found || {
          group: "Selecionados",
          label: labelsFromValue([v])[0] || "—",
          value: v,
        };
      })}
      onChange={(_, newValue) => {
        const next = (newValue || [])
          .map((o) => (typeof o === "string" ? normalizeInputToValue(o) : o?.value ?? o))
          .filter(Boolean);

        onChange(next);

        const lbls = labelsFromValue(next);
        onLabel?.(mode, lbls);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${label}${required ? " *" : ""}`}
          size="small"
          margin="dense"
          error={!!errorText}
          helperText={
            errorText ||
            helperText ||
            "Selecione (cliente/usuário/cargo) ou digite um e-mail e pressione Enter."
          }
        />
      )}
    />
  );
}

/** ===================== Schedule Interval Field ===================== */
function ScheduleIntervalField({ name, label, value, onChange, onLabel, helperText, errorText, required }) {
  const parsed = useMemo(() => {
    if (!value) return { value: "", unit: "minutes" };
    if (typeof value === "object") return { value: value.value || "", unit: value.unit || "minutes" };
    return { value: value, unit: "minutes" };
  }, [value]);

  const emitChange = (nextVal, nextUnit) => {
    const obj = { value: Number(nextVal) || "", unit: nextUnit };
    onChange(name, obj);
    if (onLabel) {
      const unitLabel = SCHEDULE_UNIT_OPTIONS.find((o) => o.value === nextUnit)?.label || nextUnit;
      onLabel(name, nextVal ? `${nextVal} ${unitLabel}` : "");
    }
  };

  const minValue = parsed.unit === "minutes" ? 5 : 1;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      <TextField
        size="small"
        margin="dense"
        label={label}
        type="number"
        value={parsed.value}
        onChange={(e) => emitChange(e.target.value, parsed.unit)}
        inputProps={{ min: minValue, step: 1 }}
        error={!!errorText}
        helperText={errorText || helperText || `Mínimo: ${minValue} ${parsed.unit === "minutes" ? "minutos" : parsed.unit === "hours" ? "hora(s)" : "dia(s)"}`}
        required={required}
        sx={{ flex: 1 }}
      />
      <TextField
        select
        size="small"
        margin="dense"
        label="Unidade"
        value={parsed.unit}
        onChange={(e) => emitChange(parsed.value, e.target.value)}
        sx={{ minWidth: 120 }}
      >
        {SCHEDULE_UNIT_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </TextField>
    </Box>
  );
}

/** ===================== Helpers p/ Select labels ===================== */
const matchOption = (options, v) => {
  const sv = String(v ?? "");
  if (!sv) return null;
  return (
    options.find((o) => String(o.value) === sv) ||
    options.find((o) => {
      const a = Number(o.value);
      const b = Number(v);
      return Number.isFinite(a) && Number.isFinite(b) && a === b;
    }) ||
    null
  );
};

const buildLabelsFromValue = (options, val, multiple) => {
  if (multiple) {
    const arr = Array.isArray(val)
      ? val
      : val != null && val !== ""
      ? [val]
      : [];
    return arr.map((v) => matchOption(options, v)?.label ?? String(v)).filter(Boolean);
  }
  if (val == null || val === "") return "";
  return matchOption(options, val)?.label ?? String(val);
};

function SelectField({
  name,
  field,
  label,
  multiple,
  value,
  errText,
  helperText,
  required,
  isLoadingAny,
  options,
  onChange,
  onLabel,
  formState,
}) {
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : value != null && value !== ""
      ? [value]
      : []
    : value ?? "";

  const currentLabel = formState?.condicoes_labels?.[name];

  const shouldAutoEmit =
    !!onLabel &&
    (multiple
      ? Array.isArray(normalizedValue) && normalizedValue.length > 0
      : normalizedValue !== "" && normalizedValue != null) &&
    (currentLabel == null ||
      (typeof currentLabel === "string" && currentLabel.trim() === "") ||
      (Array.isArray(currentLabel) && currentLabel.length === 0));

  // ✅ quando opções chegam (ex.: deliveryMethods), emite label automaticamente
  useEffect(() => {
    if (!shouldAutoEmit) return;
    if (!Array.isArray(options) || options.length === 0) return;

    const lbl = buildLabelsFromValue(options, normalizedValue, multiple);
    onLabel?.(name, lbl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoEmit, options, name]);

  const emitLabel = (val) => {
    if (!onLabel) return;
    const lbl = buildLabelsFromValue(options, val, multiple);
    onLabel(name, lbl);
  };

  return (
    <FormControl fullWidth size="small" error={!!errText}>
      <InputLabel required={required}>{label}</InputLabel>
      <Select
        label={label}
        multiple={multiple}
        value={normalizedValue}
        onChange={(e) => {
          onChange(name, e.target.value);
          emitLabel(e.target.value);
        }}
        input={<OutlinedInput label={label} />}
        renderValue={
          multiple
            ? (selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected || []).map((val) => {
                    const opt = matchOption(options, val);
                    return (
                      <Chip
                        key={`${name}-${String(val)}`}
                        label={opt?.label ?? String(val)}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )
            : undefined
        }
      >
        {!multiple && (
          <MenuItem value="">{isLoadingAny ? "Carregando..." : "—"}</MenuItem>
        )}

        {isLoadingAny && options.length === 0 ? (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} /> Carregando...
          </MenuItem>
        ) : (
          options.map((opt) => (
            <MenuItem key={`${name}-${String(opt.value)}`} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))
        )}
      </Select>

      <Collapse in={Boolean(errText || helperText)}>
        <Typography
          variant="caption"
          color={errText ? "error" : "text.secondary"}
          sx={{ mt: 0.5 }}
        >
          {errText || helperText}
        </Typography>
      </Collapse>
    </FormControl>
  );
}

export default function DynamicFields({
  schema,
  values,
  onChange,
  onLabel,
  formState,
  compact = false,
  schemaFull,
  onlyKeys,
  errorsMap = {},
}) {
  useIgnoreResizeObserverLoopError();

  const safeSchema = schema || {};
  const rootPartial = safeSchema.condicoes || safeSchema.acoes || safeSchema.fields || {};

  const rootFullSrc = schemaFull || schema || {};
  const rootFull = rootFullSrc.condicoes || rootFullSrc.acoes || rootFullSrc.fields || {};

  let entries = useMemo(() => Object.entries(rootPartial), [rootPartial]);
  const entriesFull = useMemo(() => Object.entries(rootFull), [rootFull]);

  if (Array.isArray(onlyKeys) && onlyKeys.length) {
    entries = entries.filter(([k]) => onlyKeys.includes(k));
  }

  const { data: channelsResp, isLoading: loadingChannels } = useChannels(1, 1000, "");
  const channels = channelsResp?.channels || [];

  const { data: usersResp, isLoading: loadingUsers } = useUsers(1, 1000, "");
  const users = usersResp?.users || [];

  const { data: payResp, isLoading: loadingPays } = usePaymentMethods(1, 1000, "");
  const paymentMethods = payResp?.paymentMethods || [];

  const { data: typesResp, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const rawTypes = typesResp?.types || [];

  const { data: positionsResp, isLoading: loadingPositions } = usePositions(1, 1000, "");
  const positions = positionsResp?.positions || [];

  const { data: deliveryResp, isLoading: loadingDelivery } = useDeliveryMethods(1, 1000, "");
  const deliveryMethods = deliveryResp?.deliveryMethods || [];

  const flatTypes = useMemo(() => flattenTypes(rawTypes), [rawTypes]);
  const rootTypes = useMemo(() => flatTypes.filter((t) => !t.id_pai), [flatTypes]);

  const childrenMap = useMemo(() => {
    const map = new Map();
    for (const t of flatTypes) {
      if (t.id_pai == null) continue;
      const key = Number(t.id_pai);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return map;
  }, [flatTypes]);

  const isLoadingAny =
    loadingChannels ||
    loadingUsers ||
    loadingPays ||
    loadingTypes ||
    loadingPositions ||
    loadingDelivery;

  const getOptionsFor = (name, field) => {
    switch (field?.object) {
      case "origensdeleads": {
        const list =
          field.tipo != null
            ? (channels || []).filter((c) => Number(c.tipo) === Number(field.tipo))
            : channels || [];
        return addStaticOptions(field.options, mapBasic(list));
      }
      case "usuarios":
        return addStaticOptions(field.options, mapBasic(users));
      case "formasdepagamento":
        return addStaticOptions(field.options, mapBasic(paymentMethods));
      case "statuslead":
        return addStaticOptions(field.options, STATUS_OPTIONS);
      case "tiposdeleads":
        return addStaticOptions(field.options, mapBasic(rootTypes));
      case "subtiposdeleads": {
        const level = getLevelFromName(name, 1);
        const parentKey = findParentKeyForSubtipo(entriesFull, level);

        const parentValRaw =
          (parentKey && values?.[parentKey]) ?? (parentKey && formState?.condicoes?.[parentKey]);

        const parentIds = Array.isArray(parentValRaw)
          ? parentValRaw
          : parentValRaw != null && parentValRaw !== ""
          ? [parentValRaw]
          : [];

        if (parentIds.length === 0) return addStaticOptions(field.options, []);
        const union = parentIds.flatMap((pid) => childrenMap.get(Number(pid)) || []);
        return addStaticOptions(field.options, mapBasic(union));
      }
      case "metodosdeentrega":
        return addStaticOptions(field.options, mapBasic(deliveryMethods));
      case "cargos":
      case "positions":
        return addStaticOptions(field.options, mapBasic(positions));
      default:
        return addStaticOptions(field?.options, []);
    }
  };

  if (!entries.length) {
    return compact ? null : (
      <Typography variant="body2" color="text.secondary">
        Este item não possui parâmetros adicionais.
      </Typography>
    );
  }

  const spacing = compact ? 1 : 2;

  return (
    <Stack spacing={spacing}>
      {entries.map(([name, field]) => {
        const type = field?.type || "text";
        const multiple = !!field?.multiple;
        const label = field?.label || name;
        const value = values?.[name];
        const errText = errorsMap?.[name] || "";

        if (type === "editor") {
          const vars = Array.isArray(field?.variables) ? field.variables : [];
          const fmt = field?.varFormat || "{{key}}";

          return (
            <Box key={name}>
              <RichEditorWithHtml
                fieldName={name}
                label={label}
                required={!!field?.required}
                value={value ?? ""}
                onChange={(html) => onChange(name, html)}
                onLabel={onLabel}
                helperText={field?.helperText}
                errorText={errText}
                variables={vars}
                varFormat={fmt}
                height={field?.height || 360}
              />
            </Box>
          );
        }

        if (type === "recipients" || type === "cc") {
          return (
            <Box key={name}>
              <RecipientsField
                label={label}
                required={!!field?.required}
                value={Array.isArray(value) ? value : []}
                onChange={(v) => onChange(name, v)}
                onLabel={onLabel}
                helperText={field?.helperText}
                errorText={errText}
                users={users}
                positions={positions}
                mode={name}
              />
            </Box>
          );
        }

        if (type === "schedule_interval") {
          return (
            <ScheduleIntervalField
              key={name}
              name={name}
              label={label}
              value={value}
              onChange={onChange}
              onLabel={onLabel}
              helperText={field?.helperText}
              errorText={errText}
              required={!!field?.required}
            />
          );
        }

        if (type === "select") {
          const options = getOptionsFor(name, field);
          return (
            <SelectField
              key={name}
              name={name}
              field={field}
              label={label}
              multiple={multiple}
              value={value}
              errText={errText}
              helperText={field?.helperText}
              required={!!field?.required}
              isLoadingAny={isLoadingAny}
              options={options}
              onChange={onChange}
              onLabel={onLabel}
              formState={formState}
            />
          );
        }

        if (type === "textarea") {
          return (
            <TextField
              key={name}
              size="small"
              margin="dense"
              label={label}
              value={value ?? ""}
              onChange={(e) => {
                onChange(name, e.target.value);
                onLabel?.(name, e.target.value);
              }}
              fullWidth
              multiline
              rows={4}
              helperText={errText || field?.helperText}
              error={!!errText}
              required={!!field?.required}
            />
          );
        }

        if (type === "tag") {
          return (
            <TextField
              key={name}
              size="small"
              margin="dense"
              label={label}
              value={value ?? ""}
              onChange={(e) => {
                onChange(name, e.target.value);
                onLabel?.(name, e.target.value);
              }}
              helperText={errText || field?.helperText || "Separe por vírgula"}
              error={!!errText}
              fullWidth
              required={!!field?.required}
            />
          );
        }

        return (
          <TextField
            key={name}
            size="small"
            margin="dense"
            label={label}
            value={value ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              onChange(name, v);
              onLabel?.(name, v);
            }}
            fullWidth
            type={field?.type === "number" ? "number" : "text"}
            helperText={errText || field?.helperText}
            error={!!errText}
            required={!!field?.required}
          />
        );
      })}
    </Stack>
  );
}
